import React, {useCallback, useRef, useState} from 'react';
import {Button, Icon} from '@gravity-ui/uikit';
import {PaperPlane} from '@gravity-ui/icons';
import {useSendMessage} from '../../../entities/conversation';
import {ConversationAudience} from '../../../entities/conversation/hooks/useConversationMessages';
import styles from './ConversationFeed.module.scss';

const MAX_LENGTH = 4000;

interface MessageComposerProps {
  appealId: string;
  audience: ConversationAudience;
}

// Черновики хранятся по переписке, а не одним общим полем: композитор при смене
// Заявки не пересоздаётся, и недописанная реплика уехала бы в чужой диалог —
// вплоть до отправки её туда одной кнопкой. Ключ начинается со Стороны:
// её значения фиксированы и двоеточия не содержат, поэтому разбор однозначен.
function draftKeyOf(appealId: string, audience: ConversationAudience): string {
  return `${audience}:${appealId}`;
}

export const MessageComposer: React.FC<MessageComposerProps> = ({appealId, audience}) => {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const {sendMessage, isSending, isSendError} = useSendMessage(appealId, audience);

  const draftKey = draftKeyOf(appealId, audience);
  const text = drafts[draftKey] ?? '';

  const setText = useCallback(
    (value: string) => setDrafts((current) => ({...current, [draftKey]: value})),
    [draftKey],
  );

  const placeholder =
    audience === 'representative'
      ? 'Напишите сообщение Администратору…'
      : 'Сообщение для Представителя…';

  const canSend = text.trim().length > 0 && !isSending;

  function handleSend() {
    if (!canSend) return;
    const sent = text.trim();
    const sentKey = draftKey;
    // Очищается черновик той переписки, куда ушла реплика, и только если в нём
    // всё ещё лежит она сама: ответ может прийти, когда пользователь уже начал
    // новый черновик — в том числе в другой переписке, ведь композитор при
    // смене Заявки не пересоздаётся.
    sendMessage(sent, () =>
      setDrafts((current) =>
        (current[sentKey] ?? '').trim() === sent ? {...current, [sentKey]: ''} : current,
      ),
    );
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className={styles.feedComposer}>
      <textarea
        ref={textareaRef}
        className={styles.feedComposerInput}
        placeholder={placeholder}
        rows={3}
        value={text}
        maxLength={MAX_LENGTH}
        disabled={isSending}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      {/* Отказ отправки отдаётся по своей Переписке: композитор при смене
          Заявки не пересоздаётся, и без этого чужая осечка выглядела бы
          осечкой текущего диалога. Без показа человек не отличал бы
          отправленное от неотправленного — поле разблокировано, черновик на
          месте — и слал бы реплику второй раз. */}
      {isSendError && (
        <div className={styles.feedComposerError} role="alert">
          Не удалось отправить сообщение. Попробуйте ещё раз
        </div>
      )}
      <div className={styles.feedComposerFoot}>
        <span className={styles.feedComposerHint}>Cmd+Enter — отправить</span>
        <Button view="action" size="m" disabled={!canSend} onClick={handleSend}>
          <Icon data={PaperPlane} size={16} />
          Отправить
        </Button>
      </div>
    </div>
  );
};
